import {ImageLoadingBehaviorOptions, ThumbnailImage} from '@wix/wix-ui-tpa/cssVars';
import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useExperiments} from '@wix/yoshi-flow-editor';
import React, {type FC, useEffect, useMemo, useRef} from 'react';
import {useSwiper, useSwiperSlide} from 'swiper/react';
import {Play} from '../../../../../icons/dist';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../../providers/globalPropsProvider';
import {SPECS} from '../../../../../specs';
import type {IMediaItem} from '../../../../../types/app-types';
import {useProductGallery} from '../../../ProductGalleryProvider/ProductGalleryProvider';
import {useHasVisibleArrows} from '../utils/hooks/useHasVisibleArrows';
import {useSlidesOverflow} from '../utils/hooks/useSlidesOverflow';
import s from './Thumbnail.scss';
import {st, classes as stylableClasses} from './Thumbnail.st.css';

export enum ThumbnailDataHooks {
  Root = 'thumbnail-root',
  Image = 'thumbnail-image',
  VideoOverlay = 'video-overlay',
}

export interface ThumbnailComponentProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  mediaItem: IMediaItem;
  index: number;
}

const ThumbnailComponent: FC<ThumbnailComponentProps> = ({globals: {product, isSEO}, t, mediaItem, index}) => {
  const {experiments} = useExperiments();
  const swiper = useSwiper();
  const {isActive} = useSwiperSlide();
  const hasVisibleArrows = useHasVisibleArrows(swiper);
  const slidesOverflow = useSlidesOverflow(swiper);
  const isMainMediaNavArrowsEnabled = experiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);
  const thumbnailsOverflow = isMainMediaNavArrowsEnabled ? slidesOverflow : hasVisibleArrows;
  const {selectedIndex, hasSelectedIndexChanged} = useProductGallery();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    /* istanbul ignore next */
    if (!buttonRef.current) {
      return;
    }
    if (selectedIndex === index && hasSelectedIndexChanged) {
      buttonRef.current.focus();
    }
  }, [selectedIndex, index, hasSelectedIndexChanged]);

  const tabIndex = useMemo(() => {
    if (!thumbnailsOverflow) {
      return 0;
    }
    return isActive ? 0 : -1;
  }, [isActive, thumbnailsOverflow]);

  const isSelected = selectedIndex === index;

  return (
    <button
      type="button"
      className={s.root}
      data-hook={ThumbnailDataHooks.Root}
      tabIndex={tabIndex}
      ref={buttonRef}
      aria-pressed={isSelected}>
      {mediaItem.mediaType === 'VIDEO' && <VideoOverlay />}
      <ThumbnailImage
        data-hook={ThumbnailDataHooks.Image}
        className={st(stylableClasses.thumbnailImage, {isSelected})}
        src={mediaItem.url}
        alt={t('productPage.gallery.thumbnail.ariaLabel', {productName: mediaItem.altText || product.name})}
        isSEOBot={isSEO}
        shouldUseLQIP
        encoding="AVIF"
        loadingBehavior={ImageLoadingBehaviorOptions.none}
        sourceHeight={mediaItem.height}
        sourceWidth={mediaItem.width}
        fluid={true}
        stretchImage={true}
      />
    </button>
  );
};

const VideoOverlay = () => (
  <div className={s.videoOverlay} data-hook={ThumbnailDataHooks.VideoOverlay}>
    <Play />
  </div>
);

export const Thumbnail = withGlobalProps(withTranslations('globals.texts')(ThumbnailComponent));
